// src/components/affirmations/AffirmationGenerator.tsx
// AI-powered personalized affirmations based on Soul File

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, RefreshCw, Heart, Copy, Check, BookmarkPlus, Bookmark, DollarSign, Flame, Leaf, Trophy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSoulFile } from '../../hooks/useSoulFile';
import { supabase } from '../../lib/supabase/browserClient';
import { useAuth } from '../../hooks/useAuth';
import { mystara } from '../../styles/mystara-system';
import { ToolHeader } from '../ui/ToolHeader';

interface SavedAffirmation {
  id: string;
  text: string;
  category: string;
  created_at: string;
  is_favorite: boolean;
}

const AFFIRMATION_CATEGORIES = [
  { key: 'identity', label: 'Who I Am', icon: Sparkles, accent: mystara.categoryAccents.identity },
  { key: 'abundance', label: 'Abundance', icon: DollarSign, accent: mystara.categoryAccents.abundance },
  { key: 'love', label: 'Love & Relationships', icon: Heart, accent: mystara.categoryAccents.love },
  { key: 'confidence', label: 'Confidence', icon: Flame, accent: mystara.categoryAccents.confidence },
  { key: 'health', label: 'Health & Vitality', icon: Leaf, accent: mystara.categoryAccents.health },
  { key: 'success', label: 'Success', icon: Trophy, accent: mystara.categoryAccents.success },
];

// Fallback affirmations if API fails
const FALLBACK_AFFIRMATIONS: Record<string, string[]> = {
  identity: [
    "I am becoming more myself every day.",
    "I am worthy of everything I desire.",
    "I trust who I am becoming.",
    "I am enough, exactly as I am right now.",
    "My authenticity is my superpower.",
  ],
  abundance: [
    "Money flows to me easily and effortlessly.",
    "I am a magnet for abundance in all forms.",
    "I deserve prosperity and I accept it now.",
    "Abundance is my natural state.",
    "I release all blocks to receiving.",
  ],
  love: [
    "I am worthy of deep, fulfilling love.",
    "Love flows to me and through me.",
    "I attract relationships that nourish my soul.",
    "I am open to giving and receiving love.",
    "My heart is open and my boundaries are healthy.",
  ],
  confidence: [
    "I trust myself completely.",
    "I am capable of achieving anything I set my mind to.",
    "I speak my truth with ease and grace.",
    "My confidence grows stronger every day.",
    "I believe in my abilities and my vision.",
  ],
  health: [
    "My body is strong, healthy, and full of energy.",
    "I nourish myself with love and good choices.",
    "Every cell in my body vibrates with health.",
    "I listen to what my body needs.",
    "I am grateful for my body and all it does for me.",
  ],
  success: [
    "Success is my natural state.",
    "I am creating the life I want.",
    "Opportunities flow to me effortlessly.",
    "I take aligned action toward my dreams.",
    "Everything is working out for my highest good.",
  ],
};

interface AffirmationGeneratorProps {
  onClose?: () => void;
  embedded?: boolean;
}

export function AffirmationGenerator({ onClose, embedded = false }: AffirmationGeneratorProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { soulFile } = useSoulFile();
  
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [currentAffirmation, setCurrentAffirmation] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [savedAffirmations, setSavedAffirmations] = useState<SavedAffirmation[]>([]);
  const [showSaved, setShowSaved] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  // Load saved affirmations
  useEffect(() => {
    if (user?.id) {
      loadSavedAffirmations();
    }
  }, [user?.id]);

  const loadSavedAffirmations = async () => {
    if (!user?.id) return;
    
    const { data, error } = await supabase
      .from('saved_affirmations')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setSavedAffirmations(data);
    }
  };

  const generateAffirmation = async (category: string) => {
    setSelectedCategory(category);
    setIsGenerating(true);
    setIsSaved(false);

    try {
      // Build context for personalized affirmation
      const context = soulFile ? {
        name: soulFile.essence.preferredName,
        values: soulFile.essence.coreValues,
        gifts: soulFile.essence.gifts,
        manifestations: soulFile.manifestations.filter(m => m.status === 'active').map(m => m.title),
        phase: soulFile.journey.currentPhase,
      } : null;

      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [{
            role: 'user',
            content: `Generate ONE powerful, personal affirmation for the category: ${category}. 
            
${context ? `Context about this person:
- Name: ${context.name || 'not provided'}
- Core values: ${context.values?.join(', ') || 'not provided'}
- Gifts: ${context.gifts?.join(', ') || 'not provided'}
- Currently manifesting: ${context.manifestations?.join(', ') || 'not provided'}
- Current phase: ${context.phase || 'illuminate'}` : ''}

Rules:
- Start with "I am" or "I" 
- Keep it under 15 words
- Make it feel personal and powerful
- No quotes around it
- Just the affirmation, nothing else`
          }],
          sessionType: 'affirmation'
        }),
      });

      const data = await res.json();
      
      if (data?.reply) {
        // Clean up the response
        let affirmation = data.reply.trim();
        // Remove quotes if present
        affirmation = affirmation.replace(/^["']|["']$/g, '');
        setCurrentAffirmation(affirmation);
      } else {
        // Use fallback
        const fallbacks = FALLBACK_AFFIRMATIONS[category] || FALLBACK_AFFIRMATIONS.identity;
        setCurrentAffirmation(fallbacks[Math.floor(Math.random() * fallbacks.length)]);
      }
    } catch (err) {
      console.error('Error generating affirmation:', err);
      // Use fallback
      const fallbacks = FALLBACK_AFFIRMATIONS[category] || FALLBACK_AFFIRMATIONS.identity;
      setCurrentAffirmation(fallbacks[Math.floor(Math.random() * fallbacks.length)]);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(currentAffirmation);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = async () => {
    if (!user?.id || !currentAffirmation || !selectedCategory) return;

    const { error } = await supabase
      .from('saved_affirmations')
      .insert({
        user_id: user.id,
        text: currentAffirmation,
        category: selectedCategory,
        is_favorite: false
      });

    if (!error) {
      setIsSaved(true);
      loadSavedAffirmations();
    }
  };

  const handleToggleFavorite = async (id: string, currentStatus: boolean) => {
    await supabase
      .from('saved_affirmations')
      .update({ is_favorite: !currentStatus })
      .eq('id', id);
    
    loadSavedAffirmations();
  };

  const handleDeleteSaved = async (id: string) => {
    await supabase
      .from('saved_affirmations')
      .delete()
      .eq('id', id);
    
    loadSavedAffirmations();
  };

  const handleClose = () => {
    if (embedded && onClose) {
      onClose();
    } else {
      navigate('/chat');
    }
  };

  const categoryConfig = selectedCategory
    ? AFFIRMATION_CATEGORIES.find(c => c.key === selectedCategory)
    : null;

  const wrapperClass = embedded
    ? "flex-1 overflow-y-auto"
    : "min-h-screen bg-gradient-to-br from-pink-100/40 via-purple-50/40 to-cyan-50/40";

  return (
    <div className={wrapperClass}>
      <ToolHeader
        title="Affirmations"
        onBack={handleClose}
        backLabel="Chat"
        rightAction={
          <button
            onClick={() => setShowSaved(!showSaved)}
            className={`p-2 rounded-xl transition-colors ${showSaved ? 'bg-white/50 text-purple-700' : 'text-purple-600 hover:bg-white/30'}`}
          >
            <Bookmark className="w-5 h-5" />
          </button>
        }
      />

      <div className="max-w-2xl mx-auto px-6">
        <AnimatePresence mode="wait">
          {showSaved ? (
            // Saved Affirmations View
            <motion.div
              key="saved"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2 className="text-xl font-semibold text-purple-900 mb-4">Saved Affirmations</h2>
              
              {savedAffirmations.length === 0 ? (
                <div className="text-center py-12 text-purple-500">
                  <Bookmark className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No saved affirmations yet</p>
                  <p className="text-sm mt-2">Generate and save affirmations to see them here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {savedAffirmations.map((aff) => (
                    <motion.div
                      key={aff.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white rounded-xl p-4 shadow-sm border border-purple-100"
                    >
                      <p className="text-purple-900 font-medium mb-2">{aff.text}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-purple-500 capitalize">{aff.category}</span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleToggleFavorite(aff.id, aff.is_favorite)}
                            className={`p-1.5 rounded-full transition-colors ${aff.is_favorite ? 'text-pink-500 bg-pink-50' : 'text-gray-400 hover:text-pink-500'}`}
                          >
                            <Heart className={`w-4 h-4 ${aff.is_favorite ? 'fill-current' : ''}`} />
                          </button>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(aff.text);
                            }}
                            className="p-1.5 rounded-full text-gray-400 hover:text-purple-500 transition-colors"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          ) : currentAffirmation ? (
            // Affirmation Display
            <motion.div
              key="affirmation"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="text-center"
            >
              {categoryConfig && (
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${categoryConfig.accent} backdrop-blur-md border border-white/50 text-sm font-medium mb-6`}>
                  <categoryConfig.icon className="w-4 h-4 text-purple-600" />
                  <span className="text-purple-800">{categoryConfig.label}</span>
                </div>
              )}
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-3xl p-8 shadow-lg border border-purple-100 mb-6"
              >
                <p className="text-2xl md:text-3xl font-medium text-purple-900 leading-relaxed">
                  "{currentAffirmation}"
                </p>
              </motion.div>

              <div className="flex justify-center gap-3 mb-8">
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200 transition-colors"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
                
                <button
                  onClick={handleSave}
                  disabled={isSaved}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                    isSaved 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-pink-100 text-pink-700 hover:bg-pink-200'
                  }`}
                >
                  {isSaved ? <Check className="w-4 h-4" /> : <BookmarkPlus className="w-4 h-4" />}
                  {isSaved ? 'Saved!' : 'Save'}
                </button>
                
                <button
                  onClick={() => generateAffirmation(selectedCategory!)}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full hover:opacity-90 transition-opacity"
                >
                  <RefreshCw className="w-4 h-4" />
                  New
                </button>
              </div>

              <button
                onClick={() => {
                  setCurrentAffirmation('');
                  setSelectedCategory(null);
                }}
                className="text-purple-500 hover:text-purple-700 text-sm"
              >
                ← Choose different category
              </button>
            </motion.div>
          ) : (
            // Category Selection
            <motion.div
              key="categories"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="text-center mb-8">
                <h2 className="text-xl font-semibold text-purple-800 mb-2">
                  What do you need today?
                </h2>
                <p className="text-purple-500 text-sm">
                  Choose a category for your personalized affirmation
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto">
                {AFFIRMATION_CATEGORIES.map((category) => {
                  const Icon = category.icon;
                  return (
                    <motion.button
                      key={category.key}
                      onClick={() => generateAffirmation(category.key)}
                      disabled={isGenerating}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`
                        group p-5 rounded-xl text-left
                        bg-gradient-to-br ${category.accent}
                        backdrop-blur-md
                        border border-white/50
                        hover:border-purple-200/70
                        hover:shadow-lg hover:shadow-purple-500/10
                        transition-all duration-200
                        disabled:opacity-50
                      `}
                    >
                      <div className="w-10 h-10 rounded-full bg-white/60 border border-white/70 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                        <Icon className="w-5 h-5 text-purple-600" />
                      </div>
                      <span className="font-medium text-purple-800">{category.label}</span>
                    </motion.button>
                  );
                })}
              </div>

              {isGenerating && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center mt-8"
                >
                  <Sparkles className="w-8 h-8 mx-auto mb-2 text-purple-500 animate-pulse" />
                  <p className="text-purple-600">Creating your affirmation...</p>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default AffirmationGenerator;
